import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listTasks } from '../api';
import type { Task } from '@jizhu/shared/types';

type Tab = 'today' | 'upcoming' | 'all';

export default function Board() {
  const [tab, setTab] = useState<Tab>('today');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const opts: { from?: number; to?: number } = {};
      const now = Date.now();
      if (tab === 'today') {
        const d = new Date();
        opts.from = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        opts.to = opts.from + 24 * 3600_000;
      } else if (tab === 'upcoming') {
        opts.from = now;
        opts.to = now + 7 * 24 * 3600_000;
      }
      const t = await listTasks(opts);
      setTasks(t.filter(x => x.status === 'pending'));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [tab]);

  const now = Date.now();
  const overdue = tasks.filter(t => t.due_at < now);
  const onTime = tasks.filter(t => t.due_at >= now);

  return (
    <div className="board">
      <header>
        <h1>📋 记住</h1>
        <div className="header-actions">
          <button type="button" className="refresh-btn" onClick={load} disabled={loading} aria-label="刷新">
            ↻
          </button>
          <Link to="/settings" className="settings-link" aria-label="设置">⚙</Link>
        </div>
      </header>
      <nav className="tabs">
        {(['today', 'upcoming', 'all'] as Tab[]).map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
            {t === 'today' ? '今天' : t === 'upcoming' ? '即将' : '全部'}
          </button>
        ))}
      </nav>
      {loading && <p>加载中…</p>}
      {error && <p className="error">{error}</p>}
      {!loading && overdue.length > 0 && (
        <section className="overdue">
          <h2>⏰ 过期 · {overdue.length}</h2>
          {overdue.map(t => <TaskCard key={t.id} task={t} />)}
        </section>
      )}
      {!loading && onTime.length > 0 && (
        <section>
          {tab === 'today' && <h2>今天</h2>}
          {onTime.map(t => <TaskCard key={t.id} task={t} />)}
        </section>
      )}
      {!loading && tasks.length === 0 && (
        <div className="empty-state">
          <h2>👋 欢迎使用记住</h2>
          <ol>
            <li>在 Windows 桌面端按 Ctrl+Alt+R 粘贴聊天(可在桌面端「设置」里改快捷键)</li>
            <li>AI 会提取待办,确认后保存</li>
            <li>到点会通过浏览器通知提醒</li>
          </ol>
          <p className="empty-caption">没有待办</p>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task }: { task: Task }) {
  const d = new Date(task.due_at);
  const isOverdue = task.due_at < Date.now();
  return (
    <Link to={`/task/${task.id}`} className={`task-card ${isOverdue ? 'overdue' : ''}`}>
      <div className="title">☐ {task.title}</div>
      <div className="meta">
        {d.getMonth() + 1}/{d.getDate()} {d.getHours()}:{String(d.getMinutes()).padStart(2, '0')}
        {task.snooze_count > 0 && ` · 推迟过 ${task.snooze_count} 次`}
      </div>
    </Link>
  );
}
